import { useMemo } from "react";
import type { AuditLogFilters } from "src/api/client-api/auditLog/auditLogFilters";
import {
    AUDIT_LOG_ACTIONS,
    type AuditLogFacetCount,
    type AuditLogFacets,
} from "src/api/client-api/auditLog/auditLogSchemas";
import { useTranslation } from "react-i18next";
import type { FacetDimension, FacetPanelLabels, FacetSelection, FacetValueTone } from "src/components/FacetFilters";
import { useAuditLogLabels } from "./useAuditLogLabels";

export type AuditLogFacetSection = {
    key: string;
    title: string;
    counts: AuditLogFacetCount[];
    selection: FacetSelection;
    domain?: string[];
    labelOf: (facet: AuditLogFacetCount) => string;
    toneOf?: (facet: AuditLogFacetCount) => FacetValueTone | undefined;
    select: (selection: FacetSelection) => AuditLogFilters;
};

type Options = {
    facets: AuditLogFacets | undefined;
    filters: AuditLogFilters;
};

const AUDIT_LOG_ACTION_TONES: Record<string, FacetValueTone> = {
    create: "success",
    update: "warning",
    delete: "error",
};

export const toFacetDimensions = (sections: AuditLogFacetSection[]): FacetDimension[] =>
    sections.map((section) => ({
        id: section.key,
        label: section.title,
        selection: section.selection,
        domain: section.domain,
        values: section.counts.map((facet) => ({
            value: facet.value,
            label: section.labelOf(facet),
            count: facet.count,
            tone: section.toneOf?.(facet),
        })),
    }));

export const useAuditLogFacetLabels = (): FacetPanelLabels => {
    const { t } = useTranslation();

    return {
        searchPlaceholder: t("facets.search.placeholder", "Search a facet"),
        counter: (shown, total) => t("facets.counter", "{{shown}} of {{total}} facets", { shown, total }),
        empty: t("settings.audit_log.facet.none", "No facet for this window."),
        valueFilterPlaceholder: t("facets.value_filter.placeholder", "Filter the values"),
        valueFilterEmpty: t("facets.value_filter.empty", "No value matches."),
        showMore: t("facets.show_more", "Show more values"),
        showLess: t("facets.show_less", "Show fewer values"),
        only: t("facets.value.only", "Only"),
        onlyValue: (value) => t("facets.value.only_value", "Only {{value}}", { value }),
    };
};

export const adaptFacetSelectionChange =
    (
        sections: AuditLogFacetSection[],
        onSectionChange: (section: AuditLogFacetSection, selection: FacetSelection) => void,
    ) =>
    (dimensionId: string, selection: FacetSelection): void => {
        const section = sections.find((candidate) => candidate.key === dimensionId);

        if (section === undefined) {
            return;
        }

        onSectionChange(section, selection);
    };

export const useAuditLogFacetSections = (options: Options): AuditLogFacetSection[] => {
    const { facets, filters } = options;
    const { t } = useTranslation();
    const labels = useAuditLogLabels();

    return useMemo(() => {
        const rawLabel = (facet: AuditLogFacetCount): string => facet.label ?? facet.value;

        return [
            {
                key: "action",
                title: t("settings.audit_log.facet.action", "Action"),
                counts: facets?.actions ?? [],
                selection: { kept: filters.action ?? [], dropped: filters.action_not ?? [] },
                domain: [...AUDIT_LOG_ACTIONS],
                labelOf: (facet) =>
                    facet.value === "create" || facet.value === "update" || facet.value === "delete" ?
                        labels.action(facet.value)
                    :   rawLabel(facet),
                toneOf: (facet) => AUDIT_LOG_ACTION_TONES[facet.value],
                select: (selection) => ({
                    ...filters,
                    action: selection.kept as AuditLogFilters["action"],
                    action_not: selection.dropped as AuditLogFilters["action_not"],
                }),
            },
            {
                key: "entityType",
                title: t("settings.audit_log.facet.entity_type", "Object type"),
                counts: facets?.entityTypes ?? [],
                selection: { kept: filters.entityType ?? [], dropped: filters.entityType_not ?? [] },
                labelOf: (facet) => labels.entityType(facet.value),
                select: (selection) => ({ ...filters, entityType: selection.kept, entityType_not: selection.dropped }),
            },
            {
                key: "actor",
                title: t("settings.audit_log.facet.actor", "Actor"),
                counts: facets?.actors ?? [],
                selection: { kept: filters.actorId ?? [], dropped: filters.actorId_not ?? [] },
                labelOf: rawLabel,
                select: (selection) => ({ ...filters, actorId: selection.kept, actorId_not: selection.dropped }),
            },
            {
                key: "actorType",
                title: t("settings.audit_log.facet.account_type", "Account type"),
                counts: facets?.actorTypes ?? [],
                selection: { kept: filters.actorType ?? [], dropped: filters.actorType_not ?? [] },
                labelOf: (facet) => labels.actorType(facet.value),
                select: (selection) => ({ ...filters, actorType: selection.kept, actorType_not: selection.dropped }),
            },
            {
                key: "scope",
                title: t("settings.audit_log.facet.scope", "Scope"),
                counts: facets?.scopes ?? [],
                selection: { kept: filters.rootType ?? [], dropped: filters.rootType_not ?? [] },
                labelOf: (facet) => labels.scopeType(facet.value),
                select: (selection) => ({ ...filters, rootType: selection.kept, rootType_not: selection.dropped }),
            },
        ];
    }, [facets, filters, t, labels]);
};
